import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next/typescript/t";
import { DateTime } from "luxon";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormDateRangePicker,
  HookFormInfiniteScrollDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  DiscussionFiltersProps,
  DiscussionFiltersState,
} from "@/domains/community/types";
import { BaseUserResponse, UserQueryParams } from "@/redux/users/types";
import { useLazyGetTopicsQuery, useLazyGetUsersQuery } from "@/redux/service";
import { TopicQueryParams, TopicResponse } from "@/redux/comments/types";

import { useFilterCount } from "@/shared/hooks/useFilterCount";

import "../styles.css";

const discussionFiltersSchema = (
  t: TFunction,
): yup.ObjectSchema<Omit<DiscussionFiltersState, "createdBy">> =>
  yup
    .object()
    .shape({
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .typeError(t("form.validation.notValid")),
      authors: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      topics: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      createdRange: yup
        .array()
        .of(
          yup
            .mixed<DateTime>()
            .typeError(t("form.validation.notValid"))
            .nullable(),
        )
        .nullable(),
    })
    .required();

const DiscussionFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
}: DiscussionFiltersProps) => {
  const { t } = useTranslation();

  const [getTopics] = useLazyGetTopicsQuery();
  const [getUsers] = useLazyGetUsersQuery();

  const form = useForm<DiscussionFiltersState>({
    resolver: yupResolver(discussionFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: undefined,
      authors: [],
      topics: [],
      createdRange: [],
      ...defaultFilters,
    },
  });

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            suffix={<SearchOutlined />}
            placeholder={t("community.placeholders.searchDiscussions")}
          />
        ),
      },
      topics: {
        name: "topics",
        label: t("community.labels.topics"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<TopicResponse, TopicQueryParams>
            name="topics"
            control={form.control}
            fetchFn={getTopics}
            labelFieldName="title"
            valueFieldName="id"
            searchFieldName="title"
            multiple
            placeholder={t("community.placeholders.selectTopics")}
            allowClear={true}
          />
        ),
      },
      authors: {
        name: "authors",
        label: t("community.labels.authors"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<BaseUserResponse, UserQueryParams>
            name="authors"
            control={form.control}
            fetchFn={getUsers}
            labelFieldName="fullName"
            valueFieldName="id"
            searchFieldName="fullName"
            multiple
            placeholder={t("community.placeholders.selectAuthors")}
            allowClear={true}
          />
        ),
      },
      createdRange: {
        name: "createdRange",
        label: t("community.labels.createdRange"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormDateRangePicker
            name="createdRange"
            control={form.control}
            allowEmpty={[true, true]}
          />
        ),
      },
    };
  }, []);

  const activeFilterCount = useFilterCount<DiscussionFiltersState>(
    form,
    defaultFilters,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("community.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFilterCount}
      handleSearch={() => setFilters(form.getValues())}
      className="filter-layout"
    />
  );
};

export default DiscussionFilters;
